import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthScope {
  unbounded: boolean;
  academicYearId: number | null;
  teacherClassSectionIds: number[];
  teacherSubjectIds: number[];
  teacherExamScheduleIds: number[];
}

export interface AuthUser {
  id: number;
  username: string;
  fullName: string;
  role: string;
  roleId: number;
  staffId: number | null;
  mustChangePassword: boolean;
  permissions: string[];
  /** Data-ownership envelope from /auth/me. Optional for backward-compat with pre-V10 sessions. */
  scope?: AuthScope;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  academicYearId: number | null;
  setSession: (s: { token: string; refreshToken: string; user: AuthUser }) => void;
  setUser: (u: AuthUser) => void;
  setAcademicYear: (id: number) => void;
  clear: () => void;
  /** Authority shortcut: pass 'STUDENT_MGMT:VIEW' or 'ROLE_SUPER_ADMIN'. */
  hasAuthority: (code: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      academicYearId: null,
      setSession: ({ token, refreshToken, user }) => set({ token, refreshToken, user }),
      setUser: (user) => set({ user }),
      setAcademicYear: (academicYearId) => set({ academicYearId }),
      clear: () => set({ token: null, refreshToken: null, user: null, academicYearId: null }),
      hasAuthority: (code) => {
        const u = get().user;
        if (!u) return false;
        if (code.startsWith('ROLE_')) return u.role.toUpperCase().replace(/[^A-Z0-9]+/g, '_') === code.slice(5);
        return u.permissions.includes(code);
      },
    }),
    { name: 'school.auth' },
  ),
);
